import { useState } from 'react';
import type { PendingInvite } from '@heybesafe/shared';
import { acceptInvite, declineInvite } from '../api/client';
import { useSafe } from '../context/SafeContext';

export function PendingInvitesBanner({ invites }: { invites: PendingInvite[] }) {
  const { refreshInvites, refreshOrgs, resyncSafe } = useSafe();
  const [busyToken, setBusyToken] = useState<string | null>(null);
  const [errorByToken, setErrorByToken] = useState<Record<string, string>>({});

  if (invites.length === 0) return null;

  function clearError(token: string) {
    setErrorByToken((prev) => {
      if (!(token in prev)) return prev;
      const { [token]: _drop, ...rest } = prev;
      return rest;
    });
  }

  // After every accept/decline attempt — success OR failure — refresh the
  // pending list. This is what removes the stale tile when the invite was
  // revoked by the admin (server returns 404, the next list query returns
  // an empty array, the tile disappears). Without the unconditional
  // refresh, a silently-failed accept would leave the user staring at a
  // tile they can't act on.
  async function handleAccept(invite: PendingInvite) {
    setBusyToken(invite.token);
    clearError(invite.token);
    try {
      const result = await acceptInvite(invite.orgId, { token: invite.token });
      if (result.ok) {
        await Promise.all([refreshInvites(), refreshOrgs()]);
        // Now that refreshOrgs has hydrated the newly-joined family's private
        // key, force a full safe re-pull so its shared items — which predate
        // this client's lastSyncedAt — get pulled in and decrypted. Without
        // this, family items don't appear until the next unlock or full sync.
        await resyncSafe();
      } else {
        setErrorByToken((prev) => ({ ...prev, [invite.token]: result.error }));
        await refreshInvites();
      }
    } finally {
      setBusyToken(null);
    }
  }

  async function handleDecline(invite: PendingInvite) {
    setBusyToken(invite.token);
    clearError(invite.token);
    try {
      const result = await declineInvite(invite.orgId, invite.token);
      if (!result.ok) {
        setErrorByToken((prev) => ({ ...prev, [invite.token]: result.error }));
      }
      await refreshInvites();
    } finally {
      setBusyToken(null);
    }
  }

  return (
    <section aria-label="Pending family invitations" className="mb-6 space-y-3">
      {invites.map((invite) => (
        <article
          key={invite.token}
          className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-hbs-text truncate">
                You've been invited to {invite.orgName}
              </h3>
              {invite.kind === 'email-invite' ? (
                <p className="text-xs text-hbs-text-mute mt-1">
                  Your invitation is being set up — check back soon.
                </p>
              ) : (
                <p className="text-xs text-hbs-text-mute mt-1">
                  Invited {new Date(invite.createdAt).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <button
                id={`invite-decline-${invite.token}`}
                onClick={() => handleDecline(invite)}
                disabled={busyToken === invite.token}
                className="px-3 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt rounded-hbs-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Decline
              </button>
              {invite.kind !== 'email-invite' && (
                <button
                  id={`invite-accept-${invite.token}`}
                  onClick={() => handleAccept(invite)}
                  disabled={busyToken === invite.token}
                  className="px-3 py-1.5 text-xs bg-hbs-primary hover:bg-hbs-primary-hover text-white rounded-hbs-md font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Accept
                </button>
              )}
            </div>
          </div>
          {errorByToken[invite.token] && (
            <p role="alert" className="text-xs text-red-600 mt-2">
              {errorByToken[invite.token]}
            </p>
          )}
        </article>
      ))}
    </section>
  );
}
